import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";

import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import {
  createPosition,
  deletePosition,
  getPositions,
  updatePosition,
} from "../../services/positionService";
import { positionSchema } from "../../validators/positionValidators";

const Position = () => {
  const [positions, setPositions] = useState([]);
  const [filteredPositions, setFilteredPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [positionToEdit, setPositionToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);

  useEffect(() => {
    loadPositions();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      const filtered = positions.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPositions(filtered);
    }, 300);

    return () => clearTimeout(debounce);
  }, [searchTerm, positions]);

  const loadPositions = async () => {
    setLoading(true);
    try {
      const { data } = await getPositions();
      setPositions(data.data);
      setFilteredPositions(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load positions");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (positionToEdit) {
      onUpdatePosition(payload, resetForm);
    } else {
      onCreatePosition(payload, resetForm);
    }
  };

  const onCreatePosition = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createPosition(payload);

      setPositions((prev) => [...prev, data.data]);

      toast.success("Category created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Category was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setPositionToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdatePosition = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalPositions = [...positions];

      const positionIndex = originalPositions.findIndex(
        (position) => position.id === positionToEdit.id
      );

      if (positionIndex === -1) throw new Error("Position not found.");

      const { data } = await updatePosition(positionToEdit.id, payload);

      originalPositions[positionIndex] = data.data;

      setPositions(originalPositions);

      setPositionToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Position updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Position not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setPositionToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteCategory = async () => {
    const originalPositions = [...positions];

    const newPositions = originalPositions.filter(
      (position) => position.id !== positionToDelete.id
    );

    setPositions(newPositions);

    try {
      await deletePosition(positionToDelete?.id);

      toast.success("Position deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Position not deleted, try again later"
        }`
      );

      setPositions(originalPositions);
    } finally {
      setIsDeleteModalOpen(false);

      setPositionToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setPositionToEdit(null);
    setPositionToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 300,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text) => {
        const isEmpty = !text?.trim();
        const displayText = isEmpty ? "N/A" : text;

        return isEmpty ? (
          <div style={{ textAlign: "left", color: "#888" }}>{displayText}</div>
        ) : (
          <Tooltip title={text}>
            <div
              style={{
                maxWidth: 250,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {text}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Position">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Position">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Positions" subtitle1="Positions" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Position List"
            count={filteredPositions.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Position"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredPositions.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {positionToEdit ? "Edit Position" : "Add Position"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                name: positionToEdit ? positionToEdit?.name : "",
                description: positionToEdit ? positionToEdit?.description : "",
              }}
              enableReinitialize
              validationSchema={positionSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({ values, handleChange, handleBlur, errors, submitForm }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="name"
                      title="Name"
                      placeholder="E.g Vice Chancellor"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    <TextInput
                      name="description"
                      title="Description"
                      placeholder="Enter Position Description"
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteCategory}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Position;
